/**
 * Feed Sources Card Component
 * 
 * Displays the bank feed sources:
 * - File Upload (CSV, PDF, Excel)
 * - Open Banking (Mono, Plaid, Stitch)
 */

import React from 'react';
import { isOpenBankingCountry } from '../../constants/openBanking';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  FileUp,
  Plug,
  Upload,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

export const FeedSourcesCard = ({
  onFileUploadClick,
  onOpenBankingClick,
  connections = [],
  businessCountry = null
}) => {
  // Check if open banking is connected
  const activeConnections = connections.filter(c =>
    ['mono', 'plaid', 'stitch'].includes(c.source) && c.is_active
  );
  const hasOpenBanking = activeConnections.length > 0;
  // iter 908 audit - Open Banking is currently live for NG (Mono) and US (Plaid).
  // GB shows "coming soon"; all other countries show "not available in your country".
  const isOpenBankingSupported = isOpenBankingCountry(businessCountry);
  const isComingSoonCountry = businessCountry === 'GB';
  const unsupportedLabel = isComingSoonCountry
    ? 'Coming soon to the United Kingdom'
    : 'Not available in your country yet';
  const unsupportedBadgeLabel = isComingSoonCountry ? 'Coming Soon' : 'Unavailable';
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Upload className="w-5 h-5 text-[#108981]" />
          Feed Sources
        </CardTitle>
        <CardDescription>
          Connect your bank accounts or upload statements
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* File Upload */}
          <Card 
            className="border-2 border-dashed hover:border-[#108981]/50 transition-colors cursor-pointer"
            onClick={onFileUploadClick}
            data-testid="file-upload-source"
          >
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-[#108981]/10 flex items-center justify-center mx-auto mb-3">
                <FileUp className="w-6 h-6 text-[#108981]" />
              </div>
              <h3 className="font-semibold mb-1">Upload Statement</h3>
              <p className="text-sm text-muted-foreground">
                CSV, PDF, or Excel files
              </p>
              <Badge variant="outline" className="mt-3">
                Quick Start
              </Badge>
            </CardContent>
          </Card>

          {/* Open Banking */}
          <Card 
            className={`transition-colors ${isOpenBankingSupported ? 'cursor-pointer' : 'opacity-75'} ${hasOpenBanking ? 'border-green-200 bg-green-50/50' : isOpenBankingSupported ? 'hover:border-[#108981]/30' : ''}`}
            onClick={isOpenBankingSupported ? onOpenBankingClick : undefined}
            data-testid="open-banking-source"
          >
            <CardContent className="p-6 text-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${hasOpenBanking ? 'bg-green-100' : 'bg-gray-100'}`}>
                <Plug className={`w-6 h-6 ${hasOpenBanking ? 'text-green-600' : 'text-gray-400'}`} />
              </div>
              <h3 className="font-semibold mb-1">Bank Sync</h3>
              <p className="text-sm text-muted-foreground" data-testid="feed-source-open-banking-copy">
                {!isOpenBankingSupported
                  ? unsupportedLabel
                  : hasOpenBanking
                    ? `${activeConnections.length} bank${activeConnections.length > 1 ? 's' : ''} connected — auto-syncing`
                    : 'Auto-sync transactions'}
              </p>
              {hasOpenBanking ? (
                <Badge variant="outline" className="mt-3 text-green-700 border-green-300" data-testid="feed-source-add-another-bank">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Connect another bank <ArrowRight className="w-3 h-3 ml-1" />
                </Badge>
              ) : isOpenBankingSupported ? (
                <Badge variant="outline" className="mt-3">
                  Connect <ArrowRight className="w-3 h-3 ml-1" />
                </Badge>
              ) : (
                <Badge variant="outline" className="mt-3 text-amber-600 border-amber-300" data-testid="feed-source-open-banking-badge">
                  {unsupportedBadgeLabel}
                </Badge>
              )}
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>
  );
};

export default FeedSourcesCard;
